'use client';

import { useCallback, useRef } from 'react';
import { useReportWebVitals } from 'next/web-vitals';
import { useConsent } from '@/components/consent';

// Thresholds for Core Web Vitals (in ms, except CLS which is unitless)
const THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // Largest Contentful Paint
  FID: { good: 100, needsImprovement: 300 },   // First Input Delay
  CLS: { good: 0.1, needsImprovement: 0.25 },  // Cumulative Layout Shift
  FCP: { good: 1800, needsImprovement: 3000 }, // First Contentful Paint
  TTFB: { good: 800, needsImprovement: 1800 }, // Time to First Byte
  INP: { good: 200, needsImprovement: 500 },   // Interaction to Next Paint
};

type MetricName = keyof typeof THRESHOLDS;

function getMetricRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name as MetricName];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

function formatMetricValue(name: string, value: number): string {
  if (name === 'CLS') return value.toFixed(3);
  return `${Math.round(value)}ms`;
}

interface WebVitalsConfig {
  // Enable/disable specific reporting targets
  enableGTM?: boolean;
  enableGA4?: boolean;
  enableConsole?: boolean;
  enableCustomEndpoint?: boolean;
  
  // Custom endpoint URL for sending metrics
  customEndpoint?: string;
  
  // Sample rate (0-1) to reduce data volume
  sampleRate?: number;
  
  // Only report poor metrics (useful for alerting)
  onlyReportPoor?: boolean;
}

const defaultConfig: WebVitalsConfig = {
  enableGTM: true,
  enableGA4: false,
  enableConsole: process.env.NODE_ENV === 'development',
  enableCustomEndpoint: false,
  sampleRate: 1,
  onlyReportPoor: false,
};

// Custom endpoint URL from environment
const VITALS_ENDPOINT = process.env.NEXT_PUBLIC_VITALS_ENDPOINT;

export function WebVitals(props: Partial<WebVitalsConfig> = {}) {
  const config = { ...defaultConfig, ...props };
  const { has } = useConsent();

  // Next's useReportWebVitals registers a fresh set of web-vitals listeners
  // for every new callback identity and never unsubscribes. This component
  // re-renders whenever consent changes, so the callback must stay stable and
  // read the latest config and consent through refs at fire time.
  const configRef = useRef(config);
  configRef.current = config;
  const hasRef = useRef(has);
  hasRef.current = has;

  const report = useCallback((metric: Parameters<Parameters<typeof useReportWebVitals>[0]>[0]) => {
    const config = configRef.current;
    const has = hasRef.current;

    // Sample rate check
    if (config.sampleRate && config.sampleRate < 1) {
      if (Math.random() > config.sampleRate) return;
    }

    const rating = getMetricRating(metric.name, metric.value);
    
    // Only report poor metrics if configured
    if (config.onlyReportPoor && rating !== 'poor') return;

    // Enhanced metric data
    const metricData = {
      name: metric.name,
      value: metric.value,
      rating,
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
      formattedValue: formatMetricValue(metric.name, metric.value),
      url: typeof window !== 'undefined' ? window.location.pathname : '',
      timestamp: Date.now(),
    };

    // Send to Google Tag Manager
    // Statistics-gated: the push would otherwise sit in dataLayer and be sent
    // the moment GTM loads after a later consent.
    if (config.enableGTM && has('statistics') && typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'web_vitals',
        web_vital_name: metricData.name,
        web_vital_value: Math.round(
          metricData.name === 'CLS' ? metricData.value * 1000 : metricData.value
        ),
        web_vital_id: metricData.id,
        web_vital_rating: metricData.rating,
        web_vital_delta: Math.round(metricData.delta),
      });
    }

    // Send to Google Analytics 4 (gtag)
    if (config.enableGA4 && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', metricData.name, {
        value: Math.round(
          metricData.name === 'CLS' ? metricData.value * 1000 : metricData.value
        ),
        event_category: 'Web Vitals',
        event_label: metricData.id,
        non_interaction: true,
      });
    }

    // Send to custom endpoint
    if (config.enableCustomEndpoint && VITALS_ENDPOINT) {
      // Use sendBeacon for reliable delivery
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          VITALS_ENDPOINT,
          JSON.stringify(metricData)
        );
      } else {
        // Fallback to fetch
        fetch(VITALS_ENDPOINT, {
          method: 'POST',
          body: JSON.stringify(metricData),
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        }).catch(() => {
          // Silently fail - don't impact user experience
        });
      }
    }

    // Console logging for development
    if (config.enableConsole) {
      const color = rating === 'good' ? '🟢' : rating === 'needs-improvement' ? '🟡' : '🔴';
      console.debug(
        `${color} [Web Vitals] ${metricData.name}: ${metricData.formattedValue} (${metricData.rating})`
      );
    }
  }, []);

  useReportWebVitals(report);

  return null;
}

// Type declarations are already available via @types/gtag.js or similar
